import ListActionsMenu from "../documents/ListActionsMenu";
import SifenReportModal from "../sifen-reports/SifenReportModal";
import {
  CButton,
  CButtonGroup,
  CDropdown,
  CDropdownItem,
  CDropdownToggle,
  CTooltip,
} from "@coreui/react";
import {
  AUTO_INVOICE_STATUS_APPROVED_BY_SET,
  AUTO_INVOICE_STATUS_ERROR,
  AUTO_INVOICE_STATUS_PENDING,
  AUTO_INVOICE_STATUS_RECOVERABLE_ERROR,
  AUTO_INVOICE_STATUS_RESEND_REQUESTED,
  AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR,
  AUTO_INVOICE_STATUS_UPLOADED_TO_SET,
  AutoInvoice,
} from "../../models/auto-invoice";
import React, { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  AUTO_INVOICES_DESTROY,
  AUTO_INVOICES_RETRIEVE,
  AUTO_INVOICES_UPDATE,
} from "../../auth/permissions";

const MAX_VISIBLE_ACTIONS = 3;

interface InvoiceCrudAtionsProps {
  autoInvoice: AutoInvoice;
  onCancellationClick: (_: AutoInvoice) => void;
  onUpdateClick: (_: AutoInvoice) => void;
  onDeleteClick: (_: AutoInvoice) => void;
  onConsultByCdcClick: (_: AutoInvoice) => void;
  onResendClick: (_: AutoInvoice) => void;
  onHistoryClick: (_: AutoInvoice) => void;
}

const AutoInvoiceCrudActions: React.FC<InvoiceCrudAtionsProps> = ({
  autoInvoice,
  onCancellationClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onHistoryClick,
}) => {
  const [showSifenReport, setShowSifenReport] = useState(false);
  const [currentAutoInvoice, setCurrentAutoInvoice] = useState(autoInvoice);

  useEffect(() => {
    setCurrentAutoInvoice(autoInvoice);
  }, [autoInvoice]);

  useSub(`auto_invoice_${autoInvoice.id}`, (autoInvoice: AutoInvoice) => {
    setCurrentAutoInvoice(autoInvoice);
  });

  const cancelButton = (
    <PermissionRequiredComponent permissionName={AUTO_INVOICES_UPDATE}>
      <CTooltip content="Anular">
        <CButton
          className="text-white"
          color="secondary"
          onClick={() => {
            onCancellationClick(currentAutoInvoice);
          }}
        >
          <i className="fa fa-ban"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const editButton = (
    <PermissionRequiredComponent permissionName={AUTO_INVOICES_UPDATE}>
      <CTooltip content="Editar">
        <CButton
          className="text-white"
          color="warning"
          onClick={() => {
            onUpdateClick(currentAutoInvoice);
          }}
        >
          <i className="fa fa-pencil"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const deleteButton = (
    <PermissionRequiredComponent permissionName={AUTO_INVOICES_DESTROY}>
      <CTooltip content="Eliminar">
        <CButton
          className="text-white"
          color="danger"
          onClick={() => {
            onDeleteClick(currentAutoInvoice);
          }}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const invoiceDateTime = new Date(currentAutoInvoice.createdAt!).getTime();
  const now = new Date().getTime();

  const invoiceOlderThan2Days = invoiceDateTime < now - 2 * 24 * 60 * 60 * 1000;

  const shouldShowCancelButton =
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_APPROVED_BY_SET &&
    !invoiceOlderThan2Days;

  const shouldShowEditAndDeleteButton =
    (currentAutoInvoice.status === AUTO_INVOICE_STATUS_PENDING ||
      currentAutoInvoice.status === AUTO_INVOICE_STATUS_ERROR ||
      currentAutoInvoice.status === AUTO_INVOICE_STATUS_SET_UNKNOWN_ERROR ||
      currentAutoInvoice.setResponseCode === "1001" ||
      currentAutoInvoice.setResponseCode === "4003") &&
    currentAutoInvoice.status !== AUTO_INVOICE_STATUS_RECOVERABLE_ERROR;

  const consultByCdcButton = (
    <PermissionRequiredComponent permissionName={AUTO_INVOICES_UPDATE}>
      <CTooltip content="Consultar por CDC">
        <CButton
          className="text-white"
          color="primary"
          onClick={() => {
            onConsultByCdcClick(autoInvoice);
          }}
        >
          <i className="fa fa-question-circle"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowConsultByCdcButton =
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_UPLOADED_TO_SET ||
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_ERROR ||
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_RESEND_REQUESTED ||
    currentAutoInvoice.setResponseCode === "1001";

  const resendButton = (
    <PermissionRequiredComponent permissionName={AUTO_INVOICES_UPDATE}>
      <CTooltip content="Reenviar">
        <CButton
          className="text-white"
          color="success"
          onClick={() => {
            onResendClick(currentAutoInvoice);
          }}
        >
          <i className="fa fa-refresh"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const shouldShowResendButton =
    currentAutoInvoice.status === AUTO_INVOICE_STATUS_ERROR;

  const historyButton = (
    <PermissionRequiredComponent permissionName={AUTO_INVOICES_RETRIEVE}>
      <CTooltip content="Ver Historial">
        <CButton
          className="text-white"
          color="dark"
          onClick={() => {
            onHistoryClick(currentAutoInvoice);
          }}
        >
          <i className="fa fa-history"></i>
        </CButton>
      </CTooltip>
    </PermissionRequiredComponent>
  );

  const allActions = [
    {
      element: historyButton,
      shouldShow: true,
      label: "Ver Historial",
      icon: "fa-history",
      permission: AUTO_INVOICES_RETRIEVE,
      onClick: () => onHistoryClick(currentAutoInvoice),
    },
    {
      element: cancelButton,
      shouldShow: shouldShowCancelButton,
      label: "Anular",
      icon: "fa-ban",
      permission: AUTO_INVOICES_UPDATE,
      onClick: () => onCancellationClick(currentAutoInvoice),
    },
    {
      element: editButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Editar",
      icon: "fa-pencil",
      permission: AUTO_INVOICES_UPDATE,
      onClick: () => onUpdateClick(currentAutoInvoice),
    },
    {
      element: deleteButton,
      shouldShow: shouldShowEditAndDeleteButton,
      label: "Eliminar",
      icon: "fa-trash",
      permission: AUTO_INVOICES_DESTROY,
      onClick: () => onDeleteClick(currentAutoInvoice),
    },
    {
      element: consultByCdcButton,
      shouldShow: shouldShowConsultByCdcButton,
      label: "Consultar por CDC",
      icon: "fa-question-circle",
      permission: AUTO_INVOICES_UPDATE,
      onClick: () => onConsultByCdcClick(autoInvoice),
    },
    {
      element: resendButton,
      shouldShow: shouldShowResendButton,
      label: "Reenviar",
      icon: "fa-refresh",
      permission: AUTO_INVOICES_UPDATE,
      onClick: () => onResendClick(currentAutoInvoice),
    },
    {
      element: (
        <PermissionRequiredComponent permissionName={AUTO_INVOICES_UPDATE}>
          <CTooltip content="Preparar reporte para DNIT">
            <CButton color="warning" aria-label="Preparar reporte para DNIT" onClick={() => setShowSifenReport(true)}>
              <i className="fa fa-flag"></i>
            </CButton>
          </CTooltip>
        </PermissionRequiredComponent>
      ),
      shouldShow: currentAutoInvoice.status === "cancelled",
      label: "Preparar reporte para DNIT",
      icon: "fa-flag",
      permission: AUTO_INVOICES_UPDATE,
      onClick: () => setShowSifenReport(true),
    },
  ];

  const visibleActions = allActions.filter((a) => a.shouldShow);
  const shouldCollapse = visibleActions.length > MAX_VISIBLE_ACTIONS;
  const primaryActions = shouldCollapse
    ? visibleActions.slice(0, MAX_VISIBLE_ACTIONS - 1)
    : visibleActions;
  const overflowActions = shouldCollapse
    ? visibleActions.slice(MAX_VISIBLE_ACTIONS - 1)
    : [];

  return (
    <td className="text-center">
      <CButtonGroup>
        {primaryActions.map((a, i) => (
          <React.Fragment key={i}>{a.element}</React.Fragment>
        ))}
        {shouldCollapse && (
          <CDropdown className="btn-group">
            <CTooltip content="Más opciones">
              <CDropdownToggle
                color="primary"
                className="text-white"
                caret={false}
                style={{ borderTopRightRadius: "0.25rem", borderBottomRightRadius: "0.25rem" }}
              >
                <i className="fa fa-ellipsis-v"></i>
              </CDropdownToggle>
            </CTooltip>
            <ListActionsMenu>
              {overflowActions.map((a, i) => (
                <PermissionRequiredComponent
                  key={i}
                  permissionName={a.permission}
                >
                  <CDropdownItem onClick={a.onClick}>
                    <i className={`fa ${a.icon} mr-2`}></i>
                    {a.label}
                  </CDropdownItem>
                </PermissionRequiredComponent>
              ))}
            </ListActionsMenu>
          </CDropdown>
        )}
      </CButtonGroup>
      {showSifenReport && <SifenReportModal
        show={showSifenReport}
        documentType="electronic_auto_invoice"
        documentId={currentAutoInvoice.id}
        onClose={() => setShowSifenReport(false)}
      />}
    </td>
  );
};

export default AutoInvoiceCrudActions;
